import { createFileRoute, Link } from '@tanstack/react-router';
import { DataLoadError } from '@/components/DataLoadError';
import { useState } from 'react';
import { StatCard } from '@/components/ui/StatCard';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { TENANTS } from '@/data/mock';
import { useTenants, useSourceFreshness } from '@/lib/use-admin-data';
import { cn } from '@/lib/utils';
import type { TenantStatus } from '@/types';
import {
  Globe,
  CheckCircle,
  Receipt,
  WarningOctagon,
  MagnifyingGlass,
  Plus,
  Warning,
} from '@phosphor-icons/react';
import { CreateTenantDialog } from '@/components/tenants/CreateTenantDialog';
import { TenantRowMenu } from '@/components/tenants/TenantRowMenu';
import { TenantSuspensionBadge } from '@/components/tenants/TenantAccessBadge';
import type { CreatedTenant } from '@/types';

type FilterTab = 'all' | 'healthy' | 'issues' | 'syncing';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function TenantsIndexPage() {
  const { data: rawTenants = [], isError: tenantsError } = useTenants();
  // Freshness is a SEPARATE query against a different service and datastore —
  // see useSourceFreshness. When it fails, health stays 'unknown'; it must never
  // fall back to a health-coloured state.
  const { data: freshness, isError: freshnessError, isLoading: freshnessLoading } = useSourceFreshness();

  const tenants = rawTenants.map((t) => {
    const f = freshness?.tenants?.[t.id];
    if (!f) return { ...t, status: 'unknown' as const, lastSync: '' };
    const newest = f.sources
      .map((s) => s.updatedAt)
      .filter((u): u is string => Boolean(u))
      .sort()
      .at(-1);
    return {
      ...t,
      status: (f.health === 'pending' ? 'sync_pending' : f.health) as typeof t.status,
      lastSync: newest ?? '',
    };
  });
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // Set ONLY from the resolved value of tenantsApi.createTenant, which rejects
  // on failure. Never inferred from the table below: a list that rendered
  // proves nothing about whether anything was written. (`useTenants` no longer
  // substitutes mock tenants on failure — it raises, and `tenantsError` above
  // surfaces it — but the reasoning here is unchanged and still load-bearing.)
  const [created, setCreated] = useState<CreatedTenant | null>(null);

  // The list is authoritative only when the API answered. If the freshly
  // created tenant is missing from it, say so rather than letting the operator
  // conclude the create silently failed.
  const createdVisibleInList = created ? tenants.some((t) => t.id === created.companyId) : false;

  const totalTenants = tenants.length;
  const healthySyncs = tenants.filter((t) => t.status === 'healthy').length;
  const totalTxns = tenants.reduce((sum, t) => sum + t.totalTransactions, 0);
  // 'stale' is an issue. 'unknown' is NOT — it means we could not read freshness,
  // and counting an unread source as a problem is the mirror image of counting
  // it as healthy. Both are claims we cannot support.
  const activeIssues = tenants.filter((t) => t.status === 'error' || t.status === 'stale').length;

  const filtered = tenants.filter((t) => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'healthy') return t.status === 'healthy';
    if (filter === 'issues') return t.status === 'error' || t.status === 'stale' || t.status === 'inactive';
    if (filter === 'syncing') return t.status === 'sync_pending';
    return true;
  });

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'healthy', label: 'Healthy' },
    { key: 'issues', label: 'Issues' },
    { key: 'syncing', label: 'Syncing' },
  ];

  return (
    <div className="space-y-6">
      {tenantsError && <DataLoadError what="tenants" />}
      {freshnessError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-3">
          <p className="text-sm font-semibold text-amber-900">Sync freshness unavailable</p>
          <p className="mt-0.5 text-xs text-amber-800">
            Every tenant below shows <strong>Unknown</strong> sync status. That is the read failing,
            not a statement about the pipelines. Freshness is read from BigQuery via analytics-api.
          </p>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Tenants</h2>
          <p className="text-slate-500 text-sm mt-1">Manage tenant configurations, data sources, and sync health.</p>
        </div>
        <button
          onClick={() => {
            setCreated(null);
            setShowCreate(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          <Plus size={18} weight="regular" />
          New tenant
        </button>
      </div>

      {/* Created — reported from the API response, not from the table */}
      {created && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="regular" className="mt-0.5 shrink-0 text-emerald-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-900">
                Created {created.name} —{' '}
                <code className="font-mono">{created.companyId}</code>
              </p>
              <p className="mt-0.5 text-xs text-emerald-800">
                {created.plan} · {created.source.replace('_', ' ')} · access {created.accessStatus}. The id is
                permanent. Next: invite the first user from the tenant page.
              </p>
              {!createdVisibleInList && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
                  <Warning size={14} weight="regular" className="mt-0.5 shrink-0" />
                  It is not in the list below yet. The tenant WAS created — the list may still be
                  refreshing, or may have failed to load (the banner above says so if it did).
                </p>
              )}
              <Link
                to="/tenants/$tenantId"
                params={{ tenantId: created.companyId }}
                className="mt-2 inline-block text-sm font-medium text-emerald-800 underline hover:text-emerald-900"
              >
                Open {created.companyId}
              </Link>
            </div>
            <button
              onClick={() => setCreated(null)}
              className="text-xs font-medium text-emerald-700 hover:text-emerald-900"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Tenants" value={totalTenants} icon={Globe} iconColor="text-blue-600" />
        <StatCard label="Healthy Syncs" value={healthySyncs} icon={CheckCircle} iconColor="text-green-600" />
        <StatCard
          label="Total Transactions"
          value={totalTxns.toLocaleString()}
          icon={Receipt}
          iconColor="text-primary"
        />
        <StatCard label="Active Issues" value={activeIssues} icon={WarningOctagon} iconColor="text-amber-500" />
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass
            size={20}
            weight="regular"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            placeholder="Search tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={cn(
                'px-4 py-1.5 text-sm font-medium rounded-md transition',
                filter === tab.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-6 py-3">Tenant</th>
              <th className="text-left px-6 py-3">BQ Project ID</th>
              <th className="text-left px-6 py-3">Active Sources</th>
              <th className="text-right px-6 py-3">Total Customers</th>
              <th className="text-right px-6 py-3">Total Transactions</th>
              <th className="text-left px-6 py-3">Last Sync</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-center px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((tenant) => (
              <tr
                key={tenant.id}
                className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition group"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Link
                      to="/tenants/$tenantId"
                      params={{ tenantId: tenant.id }}
                      className="flex items-center gap-3 hover:text-primary transition"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                        {getInitials(tenant.name)}
                      </div>
                      <span className="text-sm font-semibold text-slate-900 group-hover:text-primary transition">
                        {tenant.name}
                      </span>
                    </Link>
                    {/* Beside the NAME, not in the Status column — Status is sync
                        health, which is orthogonal: a suspended tenant keeps
                        syncing perfectly. Renders only for a tenant known to be
                        suspended; see TenantSuspensionBadge for why an absent
                        badge never means "active". */}
                    <TenantSuspensionBadge status={tenant.accessStatus} />
                  </div>
                </td>
                <td className="px-6 py-4 text-sm font-mono text-slate-500">
                  {tenant.bqProjectId ?? <span className="text-slate-300">not configured</span>}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
                    {tenant.activeSources}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-700 text-right font-medium">
                  {tenant.totalCustomers.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-slate-700 text-right font-medium">
                  {tenant.totalTransactions.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">
                  {tenant.lastSync
                    ? new Date(tenant.lastSync).toLocaleString()
                    : <span className="text-slate-300">{freshnessLoading ? 'loading…' : 'unknown'}</span>}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge variant={tenant.status} />
                </td>
                <td className="px-6 py-4 text-center">
                  <TenantRowMenu tenant={tenant} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/30">
          <p className="text-xs text-slate-400">
            Showing {filtered.length} of {totalTenants} tenants
          </p>
          <div className="flex items-center gap-1">
            <button className="px-3 py-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition" disabled>
              Previous
            </button>
            <button className="px-2.5 py-1 text-xs font-bold text-white bg-primary rounded-md">1</button>
            <button className="px-3 py-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition" disabled>
              Next
            </button>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateTenantDialog
          onClose={() => setShowCreate(false)}
          onCreated={(tenant) => {
            setCreated(tenant);
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/tenants/')({
  component: TenantsIndexPage,
});
